import React, { useEffect, useState } from 'react';
import { Paper, Toolbar, Typography, Link, Button } from '@mui/material';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import TitleBar from '../../titleBar/TitleBar';
import useApi from '../../hooks/useApi';
import useSnackbar from '../../hooks/useSnackbar';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Dashboard from './components/Dashboard';
import LoadingOverlay from '../../loadingOverlay/LoadingOverlay';
import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { useLocation } from 'react-router';
import Chip from '@mui/material/Chip';
import OutlinedInput from '@mui/material/OutlinedInput';
import Comments from './components/Comments';
import buildUrl from '../../buildUrl';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import ClearIcon from '@mui/icons-material/Clear';
import useSearch from '../../hooks/useSearch';
import RunningScores from './components/RunningScores';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import ExportCompleteDialog from '../../account/auditing/ExportCompleteDialog';
import ExportCommentsDialog from './components/ExportCommentsDialog';

const renderValue = (selected) => {
    const filtered = selected.filter(val => val !== 'All Years' && val !== 'All Quarters');

    if (filtered.length === 0) {
        return 'Select All';
    }

    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {filtered.map((value) => (
                <Chip key={value} label={value} size="small" />
            ))}
        </Box>
    );
};

const CustomTabPanel = (props) => {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 2, pt: 0 }}>
                    <Typography>{children}</Typography>
                </Box>
            )}
        </div>
    );
};

CustomTabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired
};

const Dialogs = { EXPORT_COMPLETE: 1 };

const NpsScore = ({ showHamburger, onClickHamburger }) => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const { handleGet, handlePost } = useApi();
    const [isLoading, setIsLoading] = useState(true);
    const { showSnackbar } = useSnackbar();
    const [tab, setTab] = React.useState(0);
    const [npsScore, setNpsScore] = useState([]);
    const [allData, setAllData] = useState([]);
    const [selectedYear, setSelectedYear] = useState(queryParams.getAll('year') || []);
    const [selectedQuarter, setSelectedQuarter] = useState(queryParams.getAll('quarter') || []);
    const [searchValue, setSearchValue] = useState(null);
    const [tempSearchValue, setTempSearchValue] = useSearch(setSearchValue);
    const [isExporting, setIsExporting] = useState(false);
    const [exportPath, setExportPath] = useState();
    const [dialog, setDialog] = useState(null);
    const [exportDialog, setExportDialog] = useState(false);

    const handleChange = (event, newValue) => {
        setTab(newValue);
    };

    const fetchNpsSurveyScore = async () => {
        setIsLoading(true);

        try {
            const params = {};

            if (selectedYear.length > 0) {
                params.year = selectedYear;
            }

            if (selectedQuarter.length > 0) {
                params.quarter = selectedQuarter;
            }

            const url = buildUrl('superadmin/accounts/nps-score', params);

            const response = await handleGet(url);
            const data = await response.json();

            setNpsScore(data.npsSurveyScore);
            setAllData(data.unfilterdNpsScore);
        }
        catch (error) {
            showSnackbar('Error fetching NPS Surveys', 'error');
        }

        setIsLoading(false);
    };

    const years = Array.isArray(allData)
        ? allData
            .reduce((acc, item) => {
                const year = new Date(item.startDate).getFullYear();
                if (!acc.includes(year)) acc.push(year);
                return acc;
            }, [])
            .sort((a, b) => b - a)
        : [];

    const quarters = (allData ?? [])
        .filter(item => item?.startDate && (selectedYear.length === 0 || new Date(item.startDate).getFullYear() === parseInt(selectedYear)))
        .map(item => item?.name?.split(' ')[0])
        .filter((value, index, self) => value && self.indexOf(value) === index);

    const handleExport = async () => {
        setExportDialog(false);
        setIsExporting(true);

        const params = {};

        if (selectedYear.length > 0) {
            params.year = selectedYear;
        }

        if (selectedQuarter.length > 0) {
            params.quarter = selectedQuarter;
        }

        if (searchValue) {
            params.search = searchValue;
        }

        const url = buildUrl('superadmin/accounts/nps-comments/export', params);

        try {
            const response = await handlePost(url);
            const data = await response.json();
            setExportPath(data.downloadPath);
            setDialog(Dialogs.EXPORT_COMPLETE);
        }
        catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
            showSnackbar('Export failed', 'error');
        }
        finally {
            setIsExporting(false);
        }
    };

    useEffect(() => {
        selectedYear.forEach(year => queryParams.append('year', year));
        selectedQuarter.forEach(quarter => queryParams.append('quarter', quarter));

        fetchNpsSurveyScore();
    }, [selectedYear, selectedQuarter]);

    if (isExporting || isLoading) {
        return (
            <LoadingOverlay />
        );
    }

    return (
        <>
            <TitleBar showHamburger={showHamburger} onClickHamburger={onClickHamburger}>
                <Breadcrumbs>
                    <Link href="/#/superadmin">Super Admin</Link>
                    <Typography>
                        <strong>NPS Score</strong>
                    </Typography>
                </Breadcrumbs>
            </TitleBar>
            <Paper sx={{ m: 2 }}>
                <Tabs value={tab} onChange={handleChange}>
                    <Tab label="Dashboard" index={0} />
                    <Tab label="Running Scores" index={1} />
                    <Tab label="Comments" index={2} />
                </Tabs>
                <Toolbar
                    disableGutters
                    sx={{ px: 2 }}
                >
                    <Box style={{ flexGrow: 1 }}>
                        <form>
                            <Grid container spacing={1} sx={{ alignItems: 'center' }}>
                                <Grid size="auto">
                                    <FormControl margin="none" sx={{ minWidth: 200 }}>
                                        <Select
                                            multiple
                                            value={selectedYear}
                                            onChange={(event) => {
                                                const value = event.target.value;

                                                if (value.includes('Select all')) {
                                                    setSelectedYear([]);
                                                }
                                                else {
                                                    setSelectedYear(value);
                                                }
                                            }}
                                            variant="outlined"
                                            size="small"
                                            displayEmpty
                                            input={<OutlinedInput />}
                                            renderValue={renderValue}
                                            placeholder="Email Address"
                                        >
                                            <MenuItem value="Select all">
                                                <Checkbox checked={selectedYear.length === 0} />
                                                <ListItemText primary="Select all" />
                                            </MenuItem>
                                            {years.map((year) => (
                                                <MenuItem key={year} value={year}>
                                                    <Checkbox checked={selectedYear.includes(year)} />
                                                    <ListItemText primary={year} />
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid size="auto">
                                    <FormControl margin="none" sx={{ minWidth: 200 }}>
                                        <Select
                                            multiple
                                            value={selectedQuarter}
                                            onChange={(event) => {
                                                const value = event.target.value;

                                                if (value.includes('Select all')) {
                                                    setSelectedQuarter([]);
                                                }
                                                else {
                                                    setSelectedQuarter(value);
                                                }
                                            }}
                                            variant="outlined"
                                            size="small"
                                            displayEmpty
                                            input={<OutlinedInput />}
                                            renderValue={renderValue}
                                        >
                                            <MenuItem value="Select all">
                                                <Checkbox checked={selectedQuarter.length === 0} />
                                                <ListItemText primary="Select all" />
                                            </MenuItem>
                                            {quarters.map(quarter => (
                                                <MenuItem key={quarter} value={quarter}>
                                                    <Checkbox checked={selectedQuarter.includes(quarter)} />
                                                    <ListItemText primary={quarter} />
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                {tab === 1 || tab === 2 ? (
                                    <>
                                        <Grid size="auto" sx={{ flexGrow: 1 }}>
                                            <FormControl margin="none" sx={{ minWidth: 200 }}>
                                                <OutlinedInput
                                                    value={tempSearchValue}
                                                    onChange={e => setTempSearchValue(e.target.value)}
                                                    placeholder="Email Address"
                                                    size="small"
                                                    sx={{ mr: 1 }}
                                                    endAdornment={(
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                onClick={() => setTempSearchValue('')}
                                                                edge="end"
                                                                disabled={!Boolean(tempSearchValue)}
                                                            >
                                                                <ClearIcon />
                                                            </IconButton>
                                                        </InputAdornment>
                                                    )}
                                                />
                                            </FormControl>
                                        </Grid>
                                        <Button onClick={() => setExportDialog(true)} sx={{ mt: 1 }}>Export</Button>
                                    </>
                                ) : null}
                            </Grid>
                        </form>
                    </Box>
                </Toolbar>
                <CustomTabPanel value={tab} index={0}>
                    <Dashboard npsScore={npsScore} />
                </CustomTabPanel>
                <CustomTabPanel value={tab} index={1}>
                    <RunningScores selectedYear={selectedYear} selectedQuarter={selectedQuarter} searchValue={searchValue} />
                </CustomTabPanel>
                <CustomTabPanel value={tab} index={2}>
                    <Comments selectedYear={selectedYear} selectedQuarter={selectedQuarter} searchValue={searchValue} />
                </CustomTabPanel>
            </Paper>
            {exportDialog && (
                <ExportCommentsDialog
                    onClose={() => setExportDialog(false)}
                    onComplete={handleExport}
                />
            )}
            {dialog === Dialogs.EXPORT_COMPLETE && (
                <ExportCompleteDialog
                    downloadPath={exportPath}
                    onClose={() => setDialog(null)}
                />
            )}
        </>
    );
};

export default NpsScore;